"use client";

import { useState, useEffect } from "react";
import "../../assets/styles/chat.css";
import Main from "../../components/layout/Main";
import CoineImage from "../../assets/images/coin-image.svg";
import { ArrowDownLeft, Send, ChevronLeft, Check } from "lucide-react";

import { useChat } from "../../context/ChatContext";
import { Button } from "antd";
import moment from "moment";
import TokenSelectModal from "./TokenSelectModal";
import { useWallet } from "../../context/WalletContext";
import { useGlobalSearch } from "../../context/GlobalSearchContext";
import { useLocation } from "react-router-dom";
import { isValidUrl } from "../../helper/functions";
import { useRef } from "react";

// const contacts = [
//   {
//     id: "1",
//     name: "Shehnaz",
//     avatar: "/placeholder.svg?height=40&width=40",
//     lastMessage: (
//       <>
//         <ArrowDownLeft style={{ marginRight: 4 }} />
//         Call ended
//       </>
//     ),
//     timestamp: "12:36 PM",
//     hasCallEnded: true,
//   },
//   {
//     id: "2",
//     name: "John",
//     avatar: "/placeholder.svg?height=40&width=40",
//     lastMessage: "What time are we there",
//     timestamp: "9:12 AM",
//     unreadCount: 1,
//     isOnline: true,
//   },
//   {
//     id: "3",
//     name: "Mueez",
//     avatar: "/placeholder.svg?height=40&width=40",
//     lastMessage: "You: I will send you the work file",
//     timestamp: "9:00 AM",
//     isOnline: true,
//   },
// ];

export default function ChatInterface() {
  const [selectedContact, setSelectedContact] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [messageText, setMessageText] = useState("");
  const [amount, setAmount] = useState();
  const [messageToken, setMessageToken] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [showSidebar, setShowSidebar] = useState(true);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const { globalSearch } = useGlobalSearch();
  const messagesEndRef = useRef(null);
  const { state } = useLocation();

  const { sendToken } = useWallet();

  // --------- Chat Context ----------
  const {
    chatUsers,
    chatMessages,
    listenToMessages,
    sendMessage,
    sendChatTokens,
    ensureParticipants,
    searchUsers,
    markUserMessagesAsRead,
    // convertAllUserNamesToLowercase,
  } = useChat();

  // ----------- handlers -----------
  const handleContactSelect = (contact) => {
    setSelectedContact(contact);
    ensureParticipants(contact.id);
    listenToMessages(contact.id);
    markUserMessagesAsRead(contact.id);
    if (isMobile) {
      setShowSidebar(false); // mobile -> open chat
    }
  };

  const handleSendChatMessage = () => {
    if (messageText.trim() && selectedContact && messageToken) {
      handleSendTokens();
      setMessageToken(false);
      setMessageText("");
    } else if (messageText.trim() && selectedContact) {
      sendMessage(selectedContact.id, messageText.trim());
      setMessageText("");
    }
  };

  const handleSendTokens = async () => {
    try {
      const response = await sendToken({
        recipient: selectedContact.id,
        amount: amount,
        notes: `Sent ${amount} Token`,
      });
      if (response.success === true) {
        sendChatTokens(selectedContact.id, amount);
        setMessageToken("");
      }
    } catch (error) {
      console.log("Error occured while sent token", error);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter") {
      handleSendChatMessage();
    }
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  // ---------- use effects -----------
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 768) {
        setIsMobile(true);
        setShowSidebar(true); // mobile start with sidebar
      } else {
        setIsMobile(false);
        setShowSidebar(true); // desktop always show sidebar
      }
    };

    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [chatMessages, selectedContact]);

  useEffect(() => {
    const fetchData = async () => {
      const filtered = await searchUsers(globalSearch.trim());
      setFilteredUsers(filtered);
    };

    fetchData();
  }, [globalSearch]);

  // When coming from connection page, auto open chat with that user
  useEffect(() => {
    // convertAllUserNamesToLowercase();
    if (state?.profile) {
      console.log("state profile-->", state.profile);
      const newUser = {
        id: state.profile._id,
        name: state.profile.name || state.profile.username,
        profileImage: state.profile.profilePic,
      };

      // If user not already in chatUsers → add it
      const alreadyExists = chatUsers.some((u) => u.id === newUser.id);

      if (!alreadyExists) {
        // Add to sidebar list
        setFilteredUsers((prev) => [...prev, newUser]);
      }

      // Auto select the user for chat
      setSelectedContact(newUser);

      // Start the chat session
      ensureParticipants(newUser.id);
      listenToMessages(newUser.id);
      // handleContactSelect(newUser);
    }
  }, [state?.profile]);

  return (
    <Main>
      <div className="section-main">
        <div className="sidebar-header">
          <div className="orange-header">Chats</div>{" "}
        </div>
        <div className="chat-container">
          {/* Desktop: Always show both */}
          {!isMobile ? (
            <>
              {/* Sidebar */}
              <div className="sidebar">
                <div className="sidebar-content">
                  <div className="contacts-list">
                    {/* If search has results → show filtered list */}
                    {filteredUsers?.length > 0 ? (
                      filteredUsers.map((contact) => {
                        return (
                          <div
                            key={contact?.id}
                            onClick={() => handleContactSelect(contact)}
                            className={`contact-item ${
                              selectedContact?.id === contact?.id
                                ? "selected"
                                : ""
                            }`}
                          >
                            <div className="avatar-container">
                              <div className="avatar">
                                {isValidUrl(contact?.profileImage) ? (
                                  <img
                                    src={contact.profileImage}
                                    alt={contact?.name}
                                    className="avatar-image"
                                  />
                                ) : (
                                  contact?.name?.[0]?.toUpperCase()
                                )}
                              </div>

                              {contact?.isOnline && (
                                <div className="online-indicator"></div>
                              )}
                            </div>

                            <div className="contact-info">
                              <div className="contact-header">
                                <h3 className="contact-name">
                                  {contact?.name || "Unknown"}
                                </h3>
                                <span className="contact-time">
                                  {moment(contact?.lastMessageTime).format(
                                    "hh:mm A"
                                  ) || ""}
                                  {contact?.unreadCount && (
                                    <div className="unread-badge">
                                      <span className="unread-count">
                                        {contact?.unreadCount}
                                      </span>
                                    </div>
                                  )}
                                </span>
                              </div>

                              <p
                                className={`contact-message ${
                                  contact?.hasCallEnded
                                    ? "call-ended"
                                    : contact?.lastMessage?.startsWith("You:")
                                    ? "sent"
                                    : ""
                                }`}
                              >
                                {contact?.lastMessage || ""}
                              </p>
                            </div>
                          </div>
                        );
                      })
                    ) : chatUsers?.length > 0 ? (
                      // If no filtered results → show all chat users
                      chatUsers.map((contact) => (
                        <div
                          key={contact?.id}
                          onClick={() => handleContactSelect(contact)}
                          className={`contact-item ${
                            selectedContact?.id === contact?.id
                              ? "selected"
                              : ""
                          }`}
                        >
                          <div className="avatar-container">
                            <div className="avatar">
                              {isValidUrl(contact?.profileImage) ? (
                                <img
                                  src={contact.profileImage}
                                  alt={contact?.name}
                                  className="avatar-image"
                                />
                              ) : (
                                contact?.name?.[0]?.toUpperCase()
                              )}
                            </div>

                            {contact?.isOnline && (
                              <div className="online-indicator"></div>
                            )}
                          </div>

                          <div className="contact-info">
                            <div className="contact-header">
                              <h3 className="contact-name">
                                {contact?.name || "Unknown"}
                              </h3>
                              <span className="contact-time">
                                {moment(contact?.lastMessageTime).format(
                                  "hh:mm A"
                                ) || ""}
                                {contact?.unreadCount > 0 && (
                                  <div className="unread-badge">
                                    <span className="unread-count">
                                      {contact?.unreadCount}
                                    </span>
                                  </div>
                                )}
                              </span>
                            </div>
                            <div id="chat-message-last-message-time">
                              {" "}
                              <small>
                                {" "}
                                {moment(contact?.lastMessageTime).format(
                                  "D, MMM YYYY"
                                ) || ""}
                              </small>
                            </div>

                            <p
                              className={`contact-message ${
                                contact?.hasCallEnded
                                  ? "call-ended"
                                  : contact?.lastMessage?.startsWith("You:")
                                  ? "sent"
                                  : ""
                              }`}
                            >
                              {contact?.lastMessage || ""}
                            </p>
                          </div>
                        </div>
                      ))
                    ) : (
                      // If both empty
                      <div className="no-users">No users found</div>
                    )}
                  </div>
                </div>
              </div>

              {/* Chat Area */}
              {selectedContact ? (
                <div className="chat-main">
                  <div className="chat-header">
                    <div className="chat-header-content">
                      <div className="chat-avatar-container">
                        <div className="chat-avatar">
                          {isValidUrl(selectedContact?.profileImage) ? (
                            <img
                              src={selectedContact.profileImage}
                              alt={selectedContact?.name}
                              className="avatar-image"
                            />
                          ) : (
                            selectedContact?.name?.[0]?.toUpperCase()
                          )}
                        </div>
                      </div>
                      <div className="chat-user-info">
                        <h2 className="chat-user-name">
                          {selectedContact?.name}
                        </h2>
                        <p className="chat-user-status">
                          {selectedContact?.isOnline ? "Online" : "Offline"}
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="messages-container">
                    <div className="messages-list">
                      {chatMessages.map((message) => {
                        const isReceived =
                          message?.senderId == selectedContact?.id;
                        const isMine = !isReceived;
                        return (
                          <div
                            key={message?.id}
                            className={`message-wrapper ${
                              message?.senderId == selectedContact?.id
                                ? "received"
                                : "sent"
                            }`}
                          >
                            <div className="message-content">
                              <div
                                className={`message-bubble ${
                                  message?.senderId == selectedContact?.id
                                    ? "received"
                                    : "sent"
                                }`}
                              >
                                {message?.type === "token" ? (
                                  <div className="token-message">
                                    <div className="token-left">
                                      <div className="token-icon-bg">
                                        <img
                                          src={CoineImage}
                                          alt="coin"
                                          className="token-icon"
                                        />
                                      </div>
                                      <div className="token-text">
                                        <span className="token-title">
                                          Token Transfer
                                        </span>
                                        <span className="token-amount">
                                          {message.text}
                                        </span>
                                      </div>
                                    </div>
                                    <div className="token-symbol">
                                      {message.symbol}
                                    </div>
                                  </div>
                                ) : (
                                  <p className="message-text">
                                    {message?.text}
                                  </p>
                                )}
                              </div>

                              <p
                                className={`message-time ${
                                  message?.senderId == selectedContact?.id
                                    ? "received"
                                    : "sent"
                                }`}
                              >
                                {moment(message?.timestamp).format("hh:mm A")}
                                {isMine && (
                                  <div className="tick-status">
                                    {message.isRead ? (
                                      <div
                                        className="double-tick"
                                        style={{ color: "#4FC3F7" }}
                                      >
                                        ✓✓
                                      </div>
                                    ) : (
                                      <div className="double-tick">✓✓</div>
                                    )}
                                  </div>
                                )}
                              </p>
                            </div>
                          </div>
                        );
                      })}
                      <div ref={messagesEndRef}></div>
                    </div>
                  </div>

                  <div className="message-input-container">
                    <div className="message-input-wrapper">
                      <div className="input-container">
                        {messageToken ? (
                          <div className="token-preview">
                            <div className="token-info">
                              <img
                                src={CoineImage}
                                alt="coin"
                                className="token-icon"
                              />
                              <div className="token-details">
                                <p className="token-title">Token Transfer</p>
                                <p className="token-amount">
                                  Sent {amount} TKN
                                </p>
                              </div>
                            </div>
                            <button
                              className="token-remove-btn"
                              onClick={() => {
                                setMessageToken(false);
                                setAmount("");
                                setMessageText("");
                              }}
                            >
                              ✕
                            </button>
                          </div>
                        ) : (
                          <input
                            type="text"
                            value={messageText}
                            onChange={(e) => setMessageText(e.target.value)}
                            placeholder="Type a message"
                            className="message-input"
                            onKeyDown={handleKeyPress}
                          />
                        )}

                        <button
                          className="send-btn"
                          onClick={handleSendChatMessage}
                        >
                          <Send />
                        </button>
                      </div>

                      <button className="tokens-btn">
                        <img alt="coin" src={CoineImage} />
                        <span
                          className="tokens-text-full"
                          onClick={() => setModalOpen(true)}
                        >
                          Send Tokens
                        </span>
                        <span className="tokens-text-short">Tokens</span>
                      </button>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="no-chat-selected">
                  <h3
                    className="no-chat-selected-title"
                    style={{ color: "white" }}
                  >
                    Select a user to start chatting
                  </h3>
                </div>
              )}
            </>
          ) : (
            // Mobile: Toggle view
            <>
              {showSidebar ? (
                <div className="sidebar">
                  <div className="sidebar-content">
                    <div className="contacts-list">
                      {/* If no chat users → show message */}
                      {(!chatUsers || chatUsers.length === 0) && (
                        <div className="no-users">
                          <p>No chat users found</p>
                        </div>
                      )}

                      {/* If chat users exist → show list */}
                      {filteredUsers?.length > 0
                        ? filteredUsers
                        : chatUsers?.map((contact) => (
                            <div
                              key={contact?.id}
                              onClick={() => handleContactSelect(contact)}
                              className={`contact-item ${
                                selectedContact?.id === contact?.id
                                  ? "selected"
                                  : ""
                              }`}
                            >
                              <div className="avatar-container">
                                <div className="avatar">
                                  {isValidUrl(contact?.profileImage) ? (
                                    <img
                                      src={contact.profileImage}
                                      alt={contact?.name}
                                      className="avatar-image"
                                    />
                                  ) : (
                                    contact?.name?.[0]?.toUpperCase()
                                  )}
                                </div>

                                {contact?.isOnline && (
                                  <div className="online-indicator"></div>
                                )}
                              </div>

                              <div className="contact-info">
                                <div className="contact-header">
                                  <h3 className="contact-name">
                                    {contact?.name || "Unknown"}
                                  </h3>

                                  <span className="contact-time">
                                    {contact?.unreadCount > 0 && (
                                      <div className="unread-badge">
                                        <span className="unread-count">
                                          {contact?.unreadCount}
                                        </span>
                                      </div>
                                    )}
                                    {contact?.timestamp || ""}
                                  </span>
                                </div>

                                <p
                                  className={`contact-message ${
                                    contact?.hasCallEnded
                                      ? "call-ended"
                                      : contact?.lastMessage?.startsWith("You:")
                                      ? "sent"
                                      : ""
                                  }`}
                                >
                                  {contact?.lastMessage || ""}
                                </p>
                              </div>
                            </div>
                          ))}
                    </div>
                  </div>
                </div>
              ) : (
                <div className="chat-main">
                  <div className="chat-header">
                    <div className="chat-header-content">
                      <Button
                        className="chatback-btn"
                        onClick={() => setShowSidebar(true)}
                      >
                        <ChevronLeft size={30} />
                      </Button>
                      <div className="chat-avatar-container">
                        <div className="chat-avatar">
                          {selectedContact.name[0]}
                        </div>
                      </div>
                      <div className="chat-user-info">
                        <h2 className="chat-user-name">
                          {selectedContact.name}
                        </h2>
                        <p className="chat-user-status">Online</p>
                      </div>
                    </div>
                  </div>
                  <div className="messages-container">
                    <div className="messages-list">
                      {chatMessages.map((message) => (
                        <div
                          key={message.id}
                          className={`message-wrapper ${
                            message.isSent ? "sent" : "received"
                          }`}
                        >
                          <div className="message-content">
                            <div
                              className={`message-bubble ${
                                message?.senderId == selectedContact?.id
                                  ? "received"
                                  : "sent"
                              }`}
                            >
                              <p className="message-text">{message.text}</p>
                            </div>
                            <p
                              className={`message-time ${
                                message?.senderId == selectedContact?.id
                                  ? "received"
                                  : "sent"
                              }`}
                            >
                              {moment(message?.timestamp).format("hh:mm A")}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div className="message-input-container">
                    <div className="message-input-wrapper">
                      <div className="input-container">
                        {messageToken ? (
                          <div className="token-preview">
                            <div className="token-info">
                              <img
                                src={CoineImage}
                                alt="coin"
                                className="token-icon"
                              />
                              <div className="token-details">
                                <p className="token-title">Token Transfer</p>
                                <p className="token-amount">
                                  Sent {amount} TKN
                                </p>
                              </div>
                            </div>
                            <button
                              className="token-remove-btn"
                              onClick={() => {
                                setMessageToken(false);
                                setAmount("");
                                setMessageText("");
                              }}
                            >
                              ✕
                            </button>
                          </div>
                        ) : (
                          <input
                            type="text"
                            value={messageText}
                            onChange={(e) => setMessageText(e.target.value)}
                            placeholder="Type a message"
                            className="message-input"
                            onKeyDown={handleKeyPress}
                          />
                        )}

                        <button
                          className="send-btn"
                          onClick={handleSendChatMessage}
                        >
                          <Send />
                        </button>
                      </div>
                      <button className="tokens-btn">
                        <img alt="coin" src={CoineImage} />
                        <span
                          className="tokens-text-full"
                          onClick={() => setModalOpen(true)}
                        >
                          Send Tokens
                        </span>
                        <span className="tokens-text-short">Tokens</span>
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
      {/* Token Modal */}
      <TokenSelectModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        onTokenSelect={(token) => {
          setMessageText(`${token} Token`);
          setAmount(token);
          setMessageToken(true);
          setModalOpen(false);
        }}
      ></TokenSelectModal>
    </Main>
  );
}
